import React from 'react'
import { Zap, Crown, AlertCircle, Calendar } from 'lucide-react'
import { useGenerationLimit } from '../hooks/useGenerationLimit'
import { useAuth } from '../context/AuthContext'

interface GenerationCounterProps {
  variant?: 'compact' | 'full'
  className?: string
}

const GenerationCounter: React.FC<GenerationCounterProps> = ({ 
  variant = 'full', 
  className = '' 
}) => {
  const { user } = useAuth()
  const { 
    loading, 
    remainingGenerations, 
    isAtLimit, 
    freeLimit, 
    freeMonthlyLimit, 
    monthlyGenerations, 
    subscriptionStatus, 
    getGenerationType 
  } = useGenerationLimit()

  if (loading) {
    return (
      <div className={`animate-pulse ${className}`}>
        <div className="h-4 bg-gray-200 rounded w-24"></div>
      </div>
    )
  }

  // Premium/Pro users get unlimited
  if (user && (subscriptionStatus === 'premium' || subscriptionStatus === 'pro')) {
    if (variant === 'compact') {
      return (
        <div className={`flex items-center space-x-1 text-sm text-green-600 ${className}`}>
          <Crown className="w-4 h-4" />
          <span>Unlimited</span>
        </div>
      )
    }
    
    return (
      <div className={`flex items-center space-x-2 px-3 py-2 bg-green-50 rounded-lg ${className}`}>
        <Crown className="w-5 h-5 text-green-600" />
        <div>
          <div className="text-sm font-medium text-green-700">Unlimited Generations</div>
          <div className="text-xs text-green-600">Premium subscriber</div>
        </div>
      </div>
    )
  }

  // Free logged-in users - show monthly limit
  if (user) {
    const monthlyRemaining = Math.max(0, freeMonthlyLimit - monthlyGenerations)
    const generationType = getGenerationType()
    
    if (variant === 'compact') {
      return (
        <div className={`flex items-center space-x-1 text-sm ${
          isAtLimit ? 'text-red-600' : monthlyRemaining <= 1 ? 'text-orange-600' : 'text-blue-600'
        } ${className}`}>
          {isAtLimit ? (
            <AlertCircle className="w-4 h-4" />
          ) : generationType === 'free_monthly' ? (
            <Calendar className="w-4 h-4" />
          ) : (
            <Zap className="w-4 h-4" />
          )}
          <span>
            {isAtLimit ? 'Need credits' : 
             generationType === 'free_monthly' ? `${monthlyRemaining} free left` :
             `${remainingGenerations} credits`}
          </span>
        </div>
      )
    }

    return (
      <div className={`px-3 py-2 rounded-lg ${
        isAtLimit 
          ? 'bg-red-50 border border-red-200' 
          : monthlyRemaining <= 1 && generationType === 'free_monthly'
          ? 'bg-orange-50 border border-orange-200' 
          : 'bg-blue-50 border border-blue-200'
      } ${className}`}>
        <div className="flex items-center space-x-2">
          {isAtLimit ? (
            <AlertCircle className="w-5 h-5 text-red-600" />
          ) : generationType === 'free_monthly' ? (
            <Calendar className="w-5 h-5 text-blue-600" />
          ) : (
            <Zap className="w-5 h-5 text-blue-600" />
          )}
          <div>
            <div className={`text-sm font-medium ${
              isAtLimit ? 'text-red-700' : 
              monthlyRemaining <= 1 && generationType === 'free_monthly' ? 'text-orange-700' : 'text-blue-700'
            }`}>
              {isAtLimit ? 'Monthly limit reached' : 
               generationType === 'free_monthly' ? `${monthlyRemaining} free generations left` :
               `${remainingGenerations} generations available`}
            </div>
            <div className={`text-xs ${
              isAtLimit ? 'text-red-600' : 
              monthlyRemaining <= 1 && generationType === 'free_monthly' ? 'text-orange-600' : 'text-blue-600'
            }`}>
              {isAtLimit 
                ? 'Purchase credits or upgrade to premium' 
                : generationType === 'free_monthly' 
                ? `${monthlyGenerations}/${freeMonthlyLimit} used this month`
                : 'Credits + monthly free generations'
              }
            </div>
          </div>
        </div>
        
        {/* Progress bar for monthly usage */}
        {generationType === 'free_monthly' && (
          <div className="mt-2">
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div 
                className={`h-2 rounded-full transition-all duration-300 ${
                  isAtLimit 
                    ? 'bg-red-500' 
                    : monthlyRemaining <= 1 
                    ? 'bg-orange-500' 
                    : 'bg-blue-500'
                }`}
                style={{ 
                  width: `${(monthlyGenerations / freeMonthlyLimit) * 100}%` 
                }}
              ></div>
            </div>
          </div>
        )}
      </div>
    )
  }

  // Anonymous user - show remaining generations
  if (variant === 'compact') {
    return (
      <div className={`flex items-center space-x-1 text-sm ${
        isAtLimit ? 'text-red-600' : remainingGenerations <= 1 ? 'text-orange-600' : 'text-gray-600'
      } ${className}`}>
        {isAtLimit ? (
          <AlertCircle className="w-4 h-4" />
        ) : (
          <Zap className="w-4 h-4" />
        )}
        <span>
          {isAtLimit ? 'Limit reached' : `${remainingGenerations} left`}
        </span>
      </div>
    )
  }

  return (
    <div className={`px-3 py-2 rounded-lg ${
      isAtLimit 
        ? 'bg-red-50 border border-red-200' 
        : remainingGenerations <= 1 
        ? 'bg-orange-50 border border-orange-200' 
        : 'bg-blue-50 border border-blue-200'
    } ${className}`}>
      <div className="flex items-center space-x-2">
        {isAtLimit ? (
          <AlertCircle className="w-5 h-5 text-red-600" />
        ) : (
          <Zap className="w-5 h-5 text-blue-600" />
        )}
        <div>
          <div className={`text-sm font-medium ${
            isAtLimit ? 'text-red-700' : remainingGenerations <= 1 ? 'text-orange-700' : 'text-blue-700'
          }`}>
            {isAtLimit ? 'Free generations used' : `${remainingGenerations} free generations left`}
          </div>
          <div className={`text-xs ${
            isAtLimit ? 'text-red-600' : remainingGenerations <= 1 ? 'text-orange-600' : 'text-blue-600'
          }`}>
            {isAtLimit 
              ? 'Sign up for 5 monthly generations + credits' 
              : `${freeLimit - remainingGenerations}/${freeLimit} used today`
            }
          </div>
        </div>
      </div>
      
      {/* Progress bar */}
      <div className="mt-2">
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div 
            className={`h-2 rounded-full transition-all duration-300 ${
              isAtLimit 
                ? 'bg-red-500' 
                : remainingGenerations <= 1 
                ? 'bg-orange-500' 
                : 'bg-blue-500'
            }`}
            style={{ 
              width: `${((freeLimit - remainingGenerations) / freeLimit) * 100}%` 
            }}
          ></div>
        </div>
      </div>
    </div>
  )
}

export default GenerationCounter 